import {DependencyList, useEffect, useRef, useState, useCallback} from 'react';
import {orgStorage} from '../services';
import {STORAGE_KEY} from '../enums';
import {Html5Qrcode} from 'html5-qrcode';
import {useDispatch} from 'react-redux';
import {ActionCreatorWithPayload} from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import {getErrorMessage} from '../helpers';
import {PaymentResult} from '../types';
import {toast} from 'react-toastify';
import {OrganizationStripeService} from '../services/organization-stripe.services';
import {AxiosInstance} from 'axios';

export const useSkipFirstRenderEffect = (callback: () => void, dependencies: DependencyList) => {
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (!isInitialMount.current) {
      callback();
    } else {
      isInitialMount.current = false;
    }
  }, dependencies);
};

export const useGetOrganizationId = () => {
  const organizationId = orgStorage.get(STORAGE_KEY.ORGANIZATION_ID);
  const teamId = orgStorage.get(STORAGE_KEY.TEAM_MEMBER_ID);

  return Number(organizationId) || Number(teamId);
};

const MAX_TIMEOUT = 2 ** 31 - 1;

export const useAuctionTimer = (endDate?: string) => {
  const [hasEnded, setHasEnded] = useState(() => (endDate ? dayjs().isAfter(dayjs(endDate)) : false));

  useEffect(() => {
    if (!endDate) {
      setHasEnded(false);
      return;
    }

    let timer: ReturnType<typeof setTimeout>;

    const schedule = () => {
      const msUntilEnd = dayjs(endDate).diff(dayjs());

      if (msUntilEnd <= 0) {
        setHasEnded(true);
        return;
      }

      setHasEnded(false);
      // якщо до кінця довше за ліміт, чекаємо частинами і перераховуємо
      timer = setTimeout(schedule, Math.min(msUntilEnd, MAX_TIMEOUT));
    };

    schedule();

    return () => clearTimeout(timer);
  }, [endDate]);

  return {hasEnded};
};

interface UseQrScannerProps {
  qrcodeRegionId: string;
  onScanSuccess: (decodedText: string) => void;
  isCameraMode: boolean;
}

export const useQrScanner = ({qrcodeRegionId, onScanSuccess, isCameraMode}: UseQrScannerProps) => {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [isScannerReady, setIsScannerReady] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    // Fix: early return when camera mode is off — also handles cleanup
    if (!isCameraMode) return;

    // Fix: isMounted guard to avoid setState after unmount during async start
    let isMounted = true;

    const startScanner = async () => {
      try {
        // Small delay to ensure the DOM element is ready
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 100);
        });

        // Bail out if component was unmounted during the delay
        if (!isMounted) return;

        const html5Qrcode = new Html5Qrcode(qrcodeRegionId);
        scannerRef.current = html5Qrcode;

        await html5Qrcode.start(
          {facingMode: 'environment'},
          {
            fps: 10,
            qrbox: (viewfinderWidth, viewfinderHeight) => {
              const minDimension = Math.min(viewfinderWidth, viewfinderHeight);
              const widthSize = Math.floor(minDimension * 0.7);
              const heightSize = Math.floor(widthSize * 1.3);

              return {width: widthSize, height: heightSize};
            },
          },
          (decodedText) => {
            // Pause the scanner to avoid spamming on a single code
            html5Qrcode.pause();
            if (isMounted) setIsPaused(true);

            onScanSuccess(decodedText);
          },
          () => {
            // Ignore per-frame "no QR found" errors — expected noise
          }
        );

        if (isMounted) {
          setIsScannerReady(true);
          setIsPaused(false);
        }
      } catch (err: any) {
        console.error('Scanner initialisation error:', err);
        if (isMounted) {
          setScanError(err?.message || 'Could not access the camera');
        }
      }
    };

    startScanner();

    return () => {
      isMounted = false;

      // Fix: .isScanning is a boolean property — check it before calling stop()
      if (scannerRef.current && scannerRef.current.isScanning) {
        scannerRef.current
          .stop()
          .then(() => {
            scannerRef.current = null;
          })
          .catch((err) => console.error('Error stopping camera:', err));
      }
    };
    // Fix: added isCameraMode to dependency array to avoid stale closure
  }, [qrcodeRegionId, isCameraMode]);

  const resumeScanner = () => {
    if (scannerRef.current && isPaused) {
      scannerRef.current.resume();
      setIsPaused(false);
    }
  };

  return {isScannerReady, scanError, resumeScanner, isPaused};
};

export const useNotificationSubscription = (
  connection: any,
  setListAction: ActionCreatorWithPayload<any>,
  addAction: ActionCreatorWithPayload<any>,
  newNotificationTarget: 'unread' | 'all' | 'both' = 'both'
) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!connection) return;

    // Підписка на отримання повного списку
    connection.on('SendList', (notifications: any) => {
      dispatch(setListAction({notifications, target: 'unread'}));
    });

    // Підписка на отримання одного нового сповіщення
    connection.on('SendNotification', (notification: any) => {
      dispatch(addAction({notification, newNotificationTarget}));
    });

    // Очищення підписок при розмонтуванні (вихід з ролі, зміна сторінки тощо)
    return () => {
      connection.off('SendList');
      connection.off('SendNotification');
    };
  }, [connection, dispatch, setListAction, addAction, newNotificationTarget]);
};

const MAX_RETRIES = 5;

interface UsePaymentStatusPollingOptions<TData> {
  onCompleted?: () => void;
  onFailed?: (data: TData) => void;
  onRefunded?: (data: TData) => void;
  maxRetries?: number;
  retryInterval?: number;
  apiInstance: AxiosInstance;
}

export const usePaymentStatusPolling = <TData>({
  onCompleted,
  onFailed,
  onRefunded,
  maxRetries = MAX_RETRIES,
  retryInterval = 3000,
  apiInstance,
}: UsePaymentStatusPollingOptions<TData>) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const checkPaymentStatus = useCallback(
    async (paymentId: string, retryCount = 0, toastId?: string | number) => {
      if (retryCount === 0) {
        toastId = toast.loading('Checking payment status...');
      }
      try {
        const {data} = await OrganizationStripeService.checkPaymentStatus(+paymentId, apiInstance);

        const status = data.status;

        if (status === PaymentResult.COMPLETED) {
          if (toastId) toast.dismiss(toastId);
          toast.success('Payment completed successfully!');
          onCompleted?.();
        } else if (status === PaymentResult.PENDING) {
          if (retryCount < maxRetries) {
            timeoutRef.current = setTimeout(() => {
              checkPaymentStatus(paymentId, retryCount + 1, toastId);
            }, retryInterval);
          } else {
            if (toastId) toast.dismiss(toastId);
            toast.info('Payment is still being processed. Please check the status in a few minutes.');
          }
        } else if (status === PaymentResult.FAILED) {
          if (toastId) toast.dismiss(toastId);
          toast.error('Payment failed!');
          // onFailed?.(data);
        } else if (status === PaymentResult.REFUNDED) {
          if (toastId) toast.dismiss(toastId);
          toast.error('Payment refunded!');
          // onRefunded?.(data);
        }
      } catch (error) {
        if (toastId) toast.dismiss(toastId);
        toast.error(getErrorMessage(error));
      }
    },
    [onCompleted, onFailed, onRefunded, maxRetries, retryInterval, apiInstance]
  );

  const startPollingFromUrl = useCallback(
    (paramName = 'paymentId', extraParamsToClean: string[] = ['session_id', 'organizationId']) => {
      const params = new URLSearchParams(window.location.search);
      const paymentId = params.get(paramName);
      if (paymentId) {
        checkPaymentStatus(paymentId);
        params.delete(paramName);
        extraParamsToClean.forEach((p) => params.delete(p));
        const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
        window.history.replaceState({}, '', newUrl);
      }
    },
    [checkPaymentStatus]
  );

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  return {checkPaymentStatus, startPollingFromUrl};
};
